import { useTheme } from 'styled-components';
import React, { useState } from 'react';

import { BADGE_POSITION } from '../constants';

import { getTitleColor, getSubtitleColor, getBadgeColor, getBadgeBackgroundColor, getImage } from './helpers';

import { Text } from '../../Text';
import { StyledOptionBox, StyledInput, StyledOption, StyledTitleBox, StyledBadgeBox } from './styles';
import { IOnChangeOption, IOptionComponent } from '../index';

export const Option = ({
    title = 'Title',
    subtitle,
    badge,
    badgeColor,
    badgeBackground,
    badgePosition = BADGE_POSITION.badge,
    badgeMaxWidth,
    badgeDataLocator,
    onClick,
    value,
    leftIcon,
    leftImage,
    rightImage,
    rightIcon,
    indicated: isIndicated,
    rightCheckbox,
    leftCheckbox,
    titleType,
    mb,
    dataLocator,
    short,
    borderWidth,
    borderColor,
    backgroundColor,
    checkedBackgroundColor,
    checkedBorderColor,
    isChecked = false,
    externalIsChecked = false,
    withExternalChecked = false,
    ...restProps
}: IOptionComponent) => {
    const theme = useTheme();
    const [innerChecked, setChecked] = useState(isChecked);
    const checked = !withExternalChecked ? innerChecked : externalIsChecked;
    const isLarge = Boolean(leftImage || rightImage);
    const defaultTitleType = isLarge ? 'large-text' : 'medium-text';

    const onChange = ({ target: { value } }: IOnChangeOption) => {
        !withExternalChecked && setChecked(!checked);

        if (onClick) onClick(value, !checked);
    };

    const isBadgeRightCorner = badgePosition === BADGE_POSITION.rightCorner;

    return (
        <StyledOptionBox
            {...restProps}
            mb={mb}
            short={short}
            backgroundColor={backgroundColor}
            borderColor={borderColor}
            checkedBackgroundColor={checkedBackgroundColor}
            checkedBorderColor={checkedBorderColor}
        >
            <StyledInput
                value={value}
                type="checkbox"
                onChange={onChange}
                indicated={isIndicated}
                borderColor={borderColor}
                data-locator={dataLocator}
                backgroundColor={backgroundColor}
                checkedBorderColor={checkedBorderColor}
                checkedBackgroundColor={checkedBackgroundColor}
                checked={checked}
            />
            <StyledOption
                isLarge={isLarge}
                leftIcon={leftIcon}
                leftImage={leftImage}
                rightIcon={rightIcon}
                rightImage={rightImage}
                indicated={isIndicated}
                checked={checked}
                rightCheckbox={rightCheckbox}
                leftCheckbox={leftCheckbox}
                short={short}
                borderColor={borderColor}
                checkedBorderColor={checkedBorderColor}
                borderWidth={borderWidth}
                backgroundColor={backgroundColor}
                checkedBackgroundColor={checkedBackgroundColor}
            >
                <>
                    <StyledTitleBox>
                        <Text
                            type={titleType || defaultTitleType}
                            bold
                            color={getTitleColor({ indicated: isIndicated, checked })}
                            mb={(subtitle && 4) || (badge && 2) || undefined}
                            ellipsis={subtitle || badge ? 1 : 2}
                        >
                            {title}
                        </Text>
                        {subtitle && !badge && (
                            <Text
                                type="small-text"
                                ellipsis={2}
                                color={getSubtitleColor({ indicated: isIndicated, checked })}
                            >
                                {subtitle}
                            </Text>
                        )}
                        {badge && (
                            <StyledBadgeBox
                                data-option="badge"
                                $paddingX={4}
                                $paddingBottom={1}
                                $paddingTop={1}
                                $borderRadius={4}
                                $badgeMaxWith={badgeMaxWidth}
                                $isBadgeRightCorner={isBadgeRightCorner}
                                $backgroundColor={getBadgeBackgroundColor({
                                    indicated: isIndicated,
                                    checked,
                                    badgeBackground,
                                })}
                            >
                                <Text
                                    dataLocator={badgeDataLocator}
                                    type="overline"
                                    color={getBadgeColor({ indicated: isIndicated, checked, badgeColor })}
                                >
                                    {badge}
                                </Text>
                            </StyledBadgeBox>
                        )}
                    </StyledTitleBox>
                    {getImage({
                        icon: leftIcon,
                        image: leftImage,
                        checkbox: leftCheckbox,
                        short,
                        borderWidth,
                        isBadgeRightCorner,
                        isLeft: true,
                    })}
                    {getImage({
                        icon: rightIcon,
                        image: rightImage,
                        checkbox: rightCheckbox,
                        short,
                        borderWidth,
                        isBadgeRightCorner,
                    })}
                </>
            </StyledOption>
        </StyledOptionBox>
    );
};
